import {
  normalisePagedResult,
  type Lead,
  type LeadQuote,
  type OgiRetrieveResponse,
  type PagedResult,
  type RawPagedResult,
} from "@ir/types";
import { adminApi, request } from "./http";

/**
 * Operational data for the admin dashboard.
 *
 * Everything goes through `/api/manage/*` on dave-service, which checks the
 * caller's session and permissions before touching Cosmos. The browser never
 * holds a function host key, so there is nothing to leak from the bundle.
 */

export interface QuoteSearchParams {
  searchTerm?: string;
  state?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: "created_at" | "updated_at" | "reference" | "type";
  sortDirection?: "asc" | "desc";
  signal?: AbortSignal;
}

export async function searchQuotes({
  searchTerm = "",
  state = "",
  pageNumber = 1,
  pageSize = 25,
  sortBy = "created_at",
  sortDirection = "desc",
  signal,
}: QuoteSearchParams = {}): Promise<PagedResult<LeadQuote>> {
  const response = await request(
    () =>
      adminApi.get<RawPagedResult<LeadQuote>>("/manage/quotes", {
        params: { searchTerm, state, pageNumber, pageSize, sortBy, sortDirection },
        signal,
      }),
    "Failed to load quotes.",
  );
  return normalisePagedResult(response.data, pageNumber, pageSize);
}

export async function getQuoteById(
  quoteId: string,
  signal?: AbortSignal,
): Promise<LeadQuote> {
  const response = await request(
    () => adminApi.get<LeadQuote>(`/manage/quotes/${encodeURIComponent(quoteId)}`, { signal }),
    "Failed to load quote details.",
  );
  return response.data;
}

export async function retryNoetica(
  quoteId: string,
  kind: "initial" | "buy",
): Promise<LeadQuote> {
  const response = await request(
    () =>
      adminApi.post<LeadQuote>(
        `/manage/quotes/${encodeURIComponent(quoteId)}/noetica/retry`,
        { kind },
      ),
    `Failed to retry the ${kind === "buy" ? "buy" : "initial"} Noetica queue.`,
  );
  return response.data;
}

export async function sendCustomerQuoteEmail(quoteId: string): Promise<LeadQuote> {
  const response = await request(
    () =>
      adminApi.post<LeadQuote>(
        `/manage/quotes/${encodeURIComponent(quoteId)}/customer-email/send`,
        {},
      ),
    "Failed to queue the customer quote email.",
  );
  return response.data;
}

export async function getQuotesByEmail(
  email: string,
  signal?: AbortSignal,
): Promise<LeadQuote[]> {
  const response = await request(
    () =>
      adminApi.get<LeadQuote[]>("/manage/quote-finder/by-email", {
        params: { email },
        signal,
      }),
    "Failed to look up quotes for that email address.",
  );
  return response.data ?? [];
}

export async function findQuotes(
  query: string,
  signal?: AbortSignal,
): Promise<LeadQuote[]> {
  const response = await request(
    () =>
      adminApi.get<LeadQuote[]>("/manage/quote-finder", {
        params: { query },
        signal,
      }),
    "Failed to find quotes for that email address or reference.",
  );
  return response.data ?? [];
}

export interface LeadSearchParams {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: "created_at" | "first_name" | "surname" | "email" | "mobile" | "type";
  sortDirection?: "asc" | "desc";
  signal?: AbortSignal;
}

export async function searchLeads({
  searchTerm = "",
  pageNumber = 1,
  pageSize = 25,
  sortBy = "created_at",
  sortDirection = "desc",
  signal,
}: LeadSearchParams = {}): Promise<PagedResult<Lead>> {
  const response = await request(
    () =>
      adminApi.get<RawPagedResult<Lead>>("/manage/leads", {
        params: { searchTerm, pageNumber, pageSize, sortBy, sortDirection },
        signal,
      }),
    "Failed to load leads.",
  );
  return normalisePagedResult(response.data, pageNumber, pageSize);
}

export async function getLeadById(
  leadId: string,
  signal?: AbortSignal,
): Promise<Lead> {
  const response = await request(
    () => adminApi.get<Lead>(`/manage/leads/${encodeURIComponent(leadId)}`, { signal }),
    "Failed to load lead details.",
  );
  return response.data;
}

export async function getFastFoodStepFormPrefill(
  leadId: string,
  signal?: AbortSignal,
): Promise<Record<string, unknown>> {
  const lead = await getLeadById(leadId, signal);
  return {
    ...(lead.info ?? {}),
    isMarketPlaceFormChecker: "marketplace",
  };
}

export async function retrieveOgiQuote(
  quoteRef: string,
  signal?: AbortSignal,
): Promise<OgiRetrieveResponse> {
  const response = await request(
    () =>
      adminApi.post<OgiRetrieveResponse>(
        "/manage/ogi/retrieve",
        { quoteRef },
        { signal },
      ),
    "Failed to retrieve that quote from OpenGI.",
  );
  return response.data;
}

/** Counts for the dashboard overview tiles. */
export interface AdminOverview {
  quotesToday: number;
  quotesTotal: number;
  leadsToday: number;
  leadsTotal: number;
  quotedCount: number;
  partialCount: number;
  failedCount: number;
  pendingCount: number;
  ogiDeliveredCount: number;
  ogiQueuedCount: number;
  providerQuoteCounts: {
    granite: number;
    inshur: number;
    kinetiq: number;
  };
}

interface AdminOverviewWireResponse {
  quotes_today?: number;
  quotes_total?: number;
  leads_today?: number;
  leads_total?: number;
  quoted_count?: number;
  partial_count?: number;
  failed_count?: number;
  pending_count?: number;
  ogi_delivered_count?: number;
  ogi_queued_count?: number;
  provider_quote_counts?: {
    granite?: number;
    inshur?: number;
    kinetiq?: number;
  };
  quotesToday?: number;
  quotesTotal?: number;
  leadsToday?: number;
  leadsTotal?: number;
  quotedCount?: number;
  partialCount?: number;
  failedCount?: number;
  pendingCount?: number;
  ogiDeliveredCount?: number;
  ogiQueuedCount?: number;
  providerQuoteCounts?: {
    granite?: number;
    inshur?: number;
    kinetiq?: number;
  };
}

export async function getOverview(signal?: AbortSignal): Promise<AdminOverview> {
  const response = await request(
    () => adminApi.get<AdminOverviewWireResponse>("/manage/overview", { signal }),
    "Failed to load dashboard statistics.",
  );

  const data = response.data;
  const providerCounts = data.provider_quote_counts ?? data.providerQuoteCounts;

  return {
    quotesToday: data.quotes_today ?? data.quotesToday ?? 0,
    quotesTotal: data.quotes_total ?? data.quotesTotal ?? 0,
    leadsToday: data.leads_today ?? data.leadsToday ?? 0,
    leadsTotal: data.leads_total ?? data.leadsTotal ?? 0,
    quotedCount: data.quoted_count ?? data.quotedCount ?? 0,
    partialCount: data.partial_count ?? data.partialCount ?? 0,
    failedCount: data.failed_count ?? data.failedCount ?? 0,
    pendingCount: data.pending_count ?? data.pendingCount ?? 0,
    ogiDeliveredCount: data.ogi_delivered_count ?? data.ogiDeliveredCount ?? 0,
    ogiQueuedCount: data.ogi_queued_count ?? data.ogiQueuedCount ?? 0,
    providerQuoteCounts: {
      granite: providerCounts?.granite ?? 0,
      inshur: providerCounts?.inshur ?? 0,
      kinetiq: providerCounts?.kinetiq ?? 0,
    },
  };
}
